/**
 * Plan cards (display only).
 *
 * BeHeard holds NO prices: plans, prices, discounts and credits are Central's
 * (docs/PLAN-AUTHORITY.md), shown at checkout in LevelUp. These cards list
 * only what BeHeard itself enforces (session limits and monthly usage
 * allowances, from the server's own tables) and product features.
 */

import { allowanceLines, freePlanLimitLines, planSessionLimitLines } from './planLimitCopy';

export type PlanTier = 'free' | 'pro' | 'pro_ai';

export interface PlanDefinition {
  tier: PlanTier;
  name: string;
  headline: string;
  description: string;
  features: string[];
  bestFor: string;
  highlights: string[];
  isPopular?: boolean;
}

export const PRICING_PLANS: PlanDefinition[] = [
  {
    tier: 'free',
    name: 'Free',
    headline: 'Get started and practice',
    description: 'Try BeHeard with limited practice and listening.',
    bestFor: 'Individual learners just getting started.',
    // Limits and allowances come from the plan tables the server enforces
    // (config/planLimitCopy.ts) — never typed here.
    features: [
      ...freePlanLimitLines(),
      ...allowanceLines('free'),
      'Limited marketplace browsing',
      'Basic AI feedback',
      'Community forum access',
    ],
    highlights: ['No credit card required'],
  },
  {
    tier: 'pro',
    name: 'Pro',
    headline: 'Level up your communication',
    description: 'More practice, deeper analytics, and portfolio basics.',
    bestFor: 'Ambitious professionals and creators.',
    features: [
      ...planSessionLimitLines('pro'),
      ...allowanceLines('pro'),
      'Full marketplace access',
      'Portfolio with basic visibility',
      'Advanced performance analytics',
      'Priority support',
    ],
    highlights: ['Best value for active users'],
    isPopular: true,
  },
  {
    tier: 'pro_ai',
    name: 'Pro + AI Voice',
    headline: 'Train with full AI power',
    description: 'AI voice coaching, advanced drills, and full marketplace exposure.',
    bestFor: 'Coaches, speakers, and sellers serious about getting hired.',
    features: [
      ...planSessionLimitLines('pro_ai'),
      ...allowanceLines('pro_ai'),
      'Premium marketplace placement',
      'Full portfolio customization',
      'Advanced analytics dashboard',
      'Custom scenario creation',
      '1-on-1 onboarding call',
    ],
    highlights: ['Designed for power users'],
  },
];

/**
 * Helper to get plan by tier.
 */
export function getPlanByTier(tier: PlanTier): PlanDefinition | undefined {
  return PRICING_PLANS.find((plan) => plan.tier === tier);
}

/**
 * Helper to check if a plan is an upgrade from current plan.
 */
export function isUpgrade(currentTier: PlanTier, targetTier: PlanTier): boolean {
  const tierOrder: PlanTier[] = ['free', 'pro', 'pro_ai'];
  return tierOrder.indexOf(targetTier) > tierOrder.indexOf(currentTier);
}

/**
 * Helper to check if a plan is a downgrade from current plan.
 */
export function isDowngrade(currentTier: PlanTier, targetTier: PlanTier): boolean {
  const tierOrder: PlanTier[] = ['free', 'pro', 'pro_ai'];
  return tierOrder.indexOf(targetTier) < tierOrder.indexOf(currentTier);
}
